// Navbar.jsx
import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import MobileMenu from '../menu/Menu';
import logo from '../../assets/navbar_logo.png';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import Footer from '../footer/Footer';
import { Outlet } from 'react-router-dom';
import SiteVisitPopup from '../SiteVisitPopup';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Projects', href: '/projects', hasDropdown: true },
    { name: 'Gallery', href: '/gallery' },
    { name: 'About', href: '/about' },
    { name: 'FAQs', href: '/faqs' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' }
  ];

  const projectSections = [
    { name: "Location", id: "location" },
    { name: "Lifestyle", id: "lifestyle" },
    { name: "Project Video", id: "project-video" },
    { name: "Floor Plans", id: "floorplans" },
    { name: "Amenities", id: "amenities" },
    { name: "Services", id: "services" }
  ];

  const handleMenuToggle = (state) => {
    if (typeof state === 'boolean') {
      setIsMenuOpen(state);
    } else {
      setIsMenuOpen(!isMenuOpen);
    }
  };

  const handlePopupOpen = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'unset';
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/projects') {
      navigate('/projects');
      setTimeout(() => {
        scrollToElement(sectionId);
      }, 100);
    } else {
      scrollToElement(sectionId);
    }
    setIsDropdownOpen(false);
  };

  const scrollToElement = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 64;
      const elementPosition = element.offsetTop - navbarHeight - 0;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <nav className="bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto py-3 px-2">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to='/'><img src={logo} alt="" className='w-auto h-10 sm:h-12' /></Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:block">
              <div className="ml-10 flex items-baseline space-x-5">
                {navLinks.map((link) => (
                  link.hasDropdown ? (
                    <div 
                      key={link.name}
                      className="relative"
                      onMouseEnter={() => setIsDropdownOpen(true)}
                      onMouseLeave={() => setIsDropdownOpen(false)}
                    >
                      <NavLink
                        to={link.href}
                        className="text-gray-900 hover:text-[var(--primarycolor)] px-3 py-2 font-medium transition-all duration-200 relative group flex items-center gap-1"
                      >
                        {link.name}
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                        />
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[var(--primarycolor)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                      </NavLink>
                      
                      {/* Dropdown Menu */}
                      <div 
                        className={`absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 transition-all duration-200 ${
                          isDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                        }`}
                      >
                        {projectSections.map((section) => (
                          <button
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className="w-full text-left px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-[var(--primarycolor)] transition-colors duration-150 text-sm font-medium"
                          >
                            {section.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <NavLink
                      key={link.name}
                      to={link.href}
                      className="text-gray-900 hover:text-[var(--primarycolor)] px-3 py-2 font-medium transition-all duration-200 relative group"
                    >
                      {link.name}
                      <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[var(--primarycolor)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                    </NavLink>
                  )
                ))}
              </div>
            </div>

            {/* Desktop CTA Button */}
            <div className="hidden lg:block">
              <button 
                onClick={handlePopupOpen}
                className="bg-[var(--primarycolor)] hover:bg-red-900 text-white px-4 py-2 rounded-full font-medium transition-all duration-500"
              >
                Book a Site Visit
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={handleMenuToggle}
                className="text-gray-700 text-[var(--primarycolor)] p-2 rounded-lg transition-all duration-200"
                aria-label="Toggle menu"
              >
                <div className="relative w-5 h-5">
                  <Menu 
                    className={`absolute inset-0 transition-all duration-300 ${
                      isMenuOpen ? 'opacity-0 rotate-180' : 'opacity-100 rotate-0'
                    }`}
                    size={30}
                  />
                  <X 
                    className={`absolute inset-0 transition-all duration-300 ${
                      isMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-180'
                    }`}
                    size={30}
                  />
                </div>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <MobileMenu 
            isMenuOpen={isMenuOpen}
            navLinks={navLinks}
            projectSections={projectSections}
            onMenuToggle={handleMenuToggle}
            onPopupOpen={handlePopupOpen}
            scrollToSection={scrollToSection}
          />
        </div>
      </nav>

      {/* Site Visit Popup */}
      <SiteVisitPopup isOpen={isPopupOpen} onClose={handlePopupClose} />

      <Outlet />
      <Footer />
    </>
  );
};

export default Navbar;